// ABOUTME: Main configuration page orchestrating all config management UI components.
// ABOUTME: Handles data fetching, pending changes state, and coordinates save/preview flows.

import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import API_BASE_URL from '../config';
import { useShop } from '../contexts/ShopContext';
import { colors } from '../theme/colors';

import ConfigPageHeader from '../components/config/ConfigPageHeader';
import LevelNavigationTabs from '../components/config/LevelNavigationTabs';
import ConfigSearchToolbar from '../components/config/ConfigSearchToolbar';
import ProductConfigTable from '../components/config/ProductConfigTable';
import CategoryConfigTable from '../components/config/CategoryConfigTable';
import GlobalDefaultsPanel from '../components/config/GlobalDefaultsPanel';
import ShopManagementSection from '../components/config/ShopManagementSection';
import ConfigCellEditDialog from '../components/config/ConfigCellEditDialog';
import AddProductDialog from '../components/config/AddProductDialog';
import AddCategoryDialog from '../components/config/AddCategoryDialog';
import SaveConfirmationDialog from '../components/config/SaveConfirmationDialog';

export default function ConfigPage() {
  const { t } = useTranslation();
  const { shops, currentShopId } = useShop();

  // Data state
  const [resolvedConfig, setResolvedConfig] = useState(null);
  const [shopConfigData, setShopConfigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('products');
  const [selectedShopId, setSelectedShopId] = useState(currentShopId || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Edit state
  const [pendingChanges, setPendingChanges] = useState([]);
  const [affectedCells, setAffectedCells] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // Save state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Product/category management state
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState(null);

  // Product/category overrides are per real shop - virtual shops have no config layer
  const realShops = React.useMemo(() => (shops || []).filter(shop => !shop.virtual), [shops]);

  // Auto-select first shop when none is selected
  useEffect(() => {
    if (!selectedShopId && realShops.length > 0) {
      setSelectedShopId(realShops[0].shop_id);
    }
  }, [realShops, selectedShopId]);

  // Fetch resolved config for selected shop
  const fetchResolvedConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requests = [
        axios.get(`${API_BASE_URL}/config/shop-config`),
      ];
      if (selectedShopId) {
        requests.push(axios.get(`${API_BASE_URL}/config/resolved/${selectedShopId}`));
      }
      const results = await Promise.all(requests);
      setShopConfigData(results[0].data);
      if (results[1]) {
        setResolvedConfig(results[1].data);
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
      setError(t('config.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [selectedShopId, t]);

  useEffect(() => {
    fetchResolvedConfig();
  }, [fetchResolvedConfig]);

  // Transform camelCase change to snake_case for API
  const toApiChange = (change) => ({
    level: change.level,
    shop_id: change.shopId,
    entity_type: change.entityType,
    entity_id: change.entityId,
    field: change.field,
    value: change.value,
  });

  // Compute affected cells when pending changes update
  useEffect(() => {
    if (pendingChanges.length === 0) {
      setAffectedCells([]);
      return;
    }
    // Use preview endpoint to get affected products
    const fetchPreview = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/config/preview`, {
          changes: pendingChanges.map(toApiChange),
        });
        setAffectedCells(res.data.affected_products || []);
      } catch (err) {
        console.error('Failed to fetch preview:', err);
      }
    };
    fetchPreview();
  }, [pendingChanges]);

  // Cell click handler
  const handleCellClick = useCallback((entity, fieldName, fieldData) => {
    const entityType = entity.product_id !== undefined ? 'product' : 'category';
    let availableLevels;
    if (fieldName === 'category' || fieldName === 'name') {
      // Name and category only live on product entries (shop L1 or general L3)
      availableLevels = [1, 3];
    } else if (activeTab === 'products') {
      availableLevels = [1, 2, 3, 4, 5];
    } else if (activeTab === 'categories') {
      availableLevels = [2, 4, 5];
    } else {
      availableLevels = [5];
    }

    setEditTarget({
      entity,
      fieldName,
      fieldData,
      entityType,
      availableLevels,
    });
    setEditDialogOpen(true);
  }, [activeTab]);

  // Apply a change from edit dialog
  const handleApplyChange = useCallback((change) => {
    setPendingChanges(prev => {
      // Remove existing change for same entity+field
      const filtered = prev.filter(c =>
        !(c.entityId === change.entityId && c.field === change.field)
      );
      // If value and level match the original, just remove the pending change
      if (change.value === change.oldValue && change.level === change.oldLevel) {
        return filtered;
      }
      return [...filtered, change];
    });
  }, []);

  // Clear override
  const handleClearOverride = useCallback((clearInfo) => {
    setPendingChanges(prev => {
      // Add a "clear" marker (value = null)
      const filtered = prev.filter(c =>
        !(c.entityId === clearInfo.entityId && c.field === clearInfo.field && c.level === clearInfo.level)
      );
      return [...filtered, { ...clearInfo, value: null, oldValue: null }];
    });
  }, []);

  // Default field change handler
  const handleDefaultFieldChange = useCallback((fieldName, newValue, oldValue) => {
    // Map default field names to their base field for level 5
    const field = fieldName;
    setPendingChanges(prev => {
      const filtered = prev.filter(c => !(c.level === 5 && c.field === field));
      if (newValue === oldValue) return filtered;
      return [...filtered, {
        level: 5,
        entityType: 'default',
        entityId: null,
        shopId: null,
        field,
        value: newValue,
        oldValue,
      }];
    });
  }, []);

  // Save handler
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/config/save`, {
        changes: pendingChanges.map(toApiChange),
      });
      setPendingChanges([]);
      setAffectedCells([]);
      setSaveDialogOpen(false);
      await fetchResolvedConfig();
    } catch (err) {
      console.error('Failed to save config:', err);
      // Validation errors (e.g. an empty or non-integer number field) carry a useful detail
      const detail = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
      setError(detail || t('config.saveError'));
    } finally {
      setSaving(false);
    }
  }, [pendingChanges, fetchResolvedConfig, t]);

  // Shop save handler
  const handleSaveShop = useCallback(async (shopData) => {
    try {
      await axios.post(`${API_BASE_URL}/config/shops`, shopData);
      // Refresh shop config
      const res = await axios.get(`${API_BASE_URL}/config/shop-config`);
      setShopConfigData(res.data);
    } catch (err) {
      console.error('Failed to save shop:', err);
      // Validation errors (e.g. virtual member/claim conflicts) carry a useful detail
      const detail = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
      setError(detail || t('config.saveError'));
    }
  }, [t]);

  // Shop delete handler
  const handleDeleteShop = useCallback(async (shopId) => {
    try {
      await axios.delete(`${API_BASE_URL}/config/shops/${shopId}`);
      // Refresh shop config
      const res = await axios.get(`${API_BASE_URL}/config/shop-config`);
      setShopConfigData(res.data);
    } catch (err) {
      console.error('Failed to delete shop:', err);
      // Conflict errors (shop still member of a virtual shop) carry a useful detail
      const detail = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
      setError(detail || t('config.saveError'));
    }
  }, [t]);

  // Add product handler
  const handleAddProduct = useCallback(async (productData) => {
    try {
      await axios.post(`${API_BASE_URL}/config/products`, productData);
      setAddProductDialogOpen(false);
      await fetchResolvedConfig();
    } catch (err) {
      if (err.response?.status === 409) {
        setError(t('config.product.duplicateId'));
      } else {
        console.error('Failed to add product:', err);
        setError(t('config.saveError'));
      }
    }
  }, [fetchResolvedConfig, t]);

  // Add category handler
  const handleAddCategory = useCallback(async (categoryData) => {
    try {
      await axios.post(`${API_BASE_URL}/config/categories`, categoryData);
      setAddCategoryDialogOpen(false);
      await fetchResolvedConfig();
    } catch (err) {
      if (err.response?.status === 409) {
        setError(t('config.category.duplicateId'));
      } else {
        console.error('Failed to add category:', err);
        setError(t('config.saveError'));
      }
    }
  }, [fetchResolvedConfig, t]);

  // Delete category handler — deletes from a specific level
  const handleDeleteCategory = useCallback(async (fromShop) => {
    if (!deleteCategoryTarget) return;
    try {
      const params = fromShop ? `?shop_id=${selectedShopId}` : '';
      await axios.delete(`${API_BASE_URL}/config/categories/${deleteCategoryTarget.id}${params}`);
      // Clear any orphaned pending changes for this category
      setPendingChanges(prev =>
        prev.filter(c => !(c.entityType === 'category' && c.entityId === deleteCategoryTarget.id))
      );
      setDeleteCategoryTarget(null);
      await fetchResolvedConfig();
    } catch (err) {
      console.error('Failed to delete category:', err);
      setDeleteCategoryTarget(null);
      if (err.response?.status === 409) {
        setError(t('config.category.inUse'));
      } else {
        setError(t('config.saveError'));
      }
    }
  }, [deleteCategoryTarget, selectedShopId, fetchResolvedConfig, t]);

  // Delete product handler
  const handleDeleteProduct = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_BASE_URL}/config/products/${deleteTarget.product_id}`);
      // Clear any orphaned pending changes for this product
      setPendingChanges(prev =>
        prev.filter(c => String(c.entityId) !== String(deleteTarget.product_id))
      );
      setDeleteTarget(null);
      await fetchResolvedConfig();
    } catch (err) {
      console.error('Failed to delete product:', err);
      setError(t('config.saveError'));
    }
  }, [deleteTarget, fetchResolvedConfig, t]);

  const handleBack = () => {
    if (currentShopId) {
      window.location.href = `/shops/${currentShopId}/dashboard`;
    } else {
      window.location.href = '/';
    }
  };

  // Build resolved categories for category tab
  const resolvedCategories = (resolvedConfig?.categories || []).map(cat => {
    // Merge with defaults for display
    return {
      ...cat,
      batch_size: cat.batch_size ?? resolvedConfig?.defaults?.default_batch_size,
      preparation_minutes: cat.preparation_minutes ?? resolvedConfig?.defaults?.default_preparation_minutes,
      baking_minutes: cat.baking_minutes ?? resolvedConfig?.defaults?.default_baking_minutes,
      unloading_minutes: cat.unloading_minutes ?? resolvedConfig?.defaults?.default_unloading_minutes,
      prediction_horizon_minutes: cat.prediction_horizon_minutes ?? resolvedConfig?.defaults?.default_prediction_horizon_minutes,
    };
  });

  // Get all shops (including disabled) from shop config
  const allShops = shopConfigData?.data?.shops || shops || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      <ConfigPageHeader
        lastSaved={resolvedConfig?.last_saved}
        pendingChangesCount={pendingChanges.length}
        onSave={() => setSaveDialogOpen(true)}
        onDiscard={() => { setPendingChanges([]); setAffectedCells([]); }}
        onBack={handleBack}
      />

      <Paper elevation={0} sx={{
        border: `1px solid ${colors.border.light}`,
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <Box sx={{ px: 2, pt: 1 }}>
          <LevelNavigationTabs activeTab={activeTab} onTabChange={setActiveTab} />
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          {(activeTab === 'products' || activeTab === 'categories') && (
            <ConfigSearchToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              categoryFilter={categoryFilter}
              onCategoryFilterChange={setCategoryFilter}
              categories={resolvedConfig?.categories || []}
              selectedShopId={selectedShopId}
              onShopChange={setSelectedShopId}
              shops={realShops}
              showShopSelector={activeTab === 'products' || activeTab === 'categories'}
              showCategoryFilter={activeTab === 'products'}
              searchPlaceholder={activeTab === 'categories'
                ? t('config.searchCategory')
                : t('config.search')}
              onAddProduct={() => setAddProductDialogOpen(true)}
              showAddProduct={activeTab === 'products'}
              onAddCategory={() => setAddCategoryDialogOpen(true)}
              showAddCategory={activeTab === 'categories'}
            />
          )}

          {activeTab === 'products' && (
            <ProductConfigTable
              products={resolvedConfig?.products || []}
              categories={resolvedConfig?.categories || []}
              searchQuery={searchQuery}
              categoryFilter={categoryFilter}
              pendingChanges={pendingChanges}
              affectedCells={affectedCells}
              onCellClick={handleCellClick}
              onDeleteProduct={(product) => setDeleteTarget(product)}
            />
          )}

          {activeTab === 'categories' && (
            <CategoryConfigTable
              categories={resolvedConfig?.categories || []}
              resolvedCategories={resolvedCategories}
              searchQuery={searchQuery}
              pendingChanges={pendingChanges}
              affectedCells={affectedCells}
              onCellClick={(cat, field) => handleCellClick(cat, field, {
                value: cat[field],
                level: cat[`${field}_level`],
                source: null,
              })}
              onDeleteCategory={(cat) => setDeleteCategoryTarget(cat)}
              shopId={selectedShopId}
            />
          )}

          {activeTab === 'defaults' && (
            <GlobalDefaultsPanel
              defaults={resolvedConfig?.defaults || {}}
              pendingChanges={pendingChanges}
              onFieldChange={handleDefaultFieldChange}
            />
          )}

          {activeTab === 'shops' && (
            <ShopManagementSection
              shops={allShops}
              onSaveShop={handleSaveShop}
              onDeleteShop={handleDeleteShop}
              categories={resolvedConfig?.categories || []}
            />
          )}
        </Box>
      </Paper>

      {/* Edit Dialog */}
      {editTarget && (
        <ConfigCellEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onApply={handleApplyChange}
          onClearOverride={handleClearOverride}
          product={editTarget.entity}
          fieldName={editTarget.fieldName}
          fieldData={editTarget.fieldData}
          shopId={selectedShopId}
          entityType={editTarget.entityType}
          availableLevels={editTarget.availableLevels}
          categories={resolvedConfig?.categories || []}
        />
      )}

      {/* Save Confirmation Dialog */}
      <SaveConfirmationDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onConfirm={handleSave}
        pendingChanges={pendingChanges}
        affectedCells={affectedCells}
        saving={saving}
      />

      {/* Add Product Dialog */}
      <AddProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onSave={handleAddProduct}
        categories={resolvedConfig?.categories || []}
      />

      {/* Add Category Dialog */}
      <AddCategoryDialog
        open={addCategoryDialogOpen}
        onClose={() => setAddCategoryDialogOpen(false)}
        onSave={handleAddCategory}
        shopId={selectedShopId}
        shopName={shops?.find(s => s.shop_id === selectedShopId)?.name}
      />

      {/* Delete Product Confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
        }}
      >
        <DialogTitle>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
            {t('config.product.deleteConfirmTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', color: colors.text.secondary }}>
            {t('config.product.deleteConfirmDescription', {
              name: deleteTarget?.name,
              id: deleteTarget?.product_id,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ textTransform: 'none' }}
          >
            {t('config.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteProduct}
            sx={{
              textTransform: 'none',
              bgcolor: colors.status.error,
              borderRadius: '8px',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            {t('config.product.deleteConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Category Confirmation — level-aware */}
      <Dialog
        open={!!deleteCategoryTarget}
        onClose={() => setDeleteCategoryTarget(null)}
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
        }}
      >
        <DialogTitle>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
            {t('config.category.deleteConfirmTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', color: colors.text.secondary, mb: deleteCategoryTarget?.exists_general && deleteCategoryTarget?.exists_shop ? 2 : 0 }}>
            {deleteCategoryTarget?.exists_general && deleteCategoryTarget?.exists_shop
              ? t('config.category.deleteLevelPrompt')
              : t('config.category.deleteConfirmDescription', {
                  name: deleteCategoryTarget?.name,
                  id: deleteCategoryTarget?.id,
                })
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteCategoryTarget(null)}
            sx={{ textTransform: 'none' }}
          >
            {t('config.cancel')}
          </Button>
          {deleteCategoryTarget?.exists_general && deleteCategoryTarget?.exists_shop ? (
            <>
              <Button
                variant="contained"
                onClick={() => handleDeleteCategory(false)}
                sx={{
                  textTransform: 'none',
                  bgcolor: colors.status.error,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#DC2626' },
                }}
              >
                {t('config.category.deleteFromGeneral')}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDeleteCategory(true)}
                sx={{
                  textTransform: 'none',
                  bgcolor: colors.status.error,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#DC2626' },
                }}
              >
                {t('config.category.deleteFromShop', {
                  shop: shops?.find(s => s.shop_id === selectedShopId)?.name || selectedShopId,
                })}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => handleDeleteCategory(
                deleteCategoryTarget?.exists_shop && !deleteCategoryTarget?.exists_general
              )}
              sx={{
                textTransform: 'none',
                bgcolor: colors.status.error,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#DC2626' },
              }}
            >
              {t('config.category.deleteConfirmButton')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
